// WHAT: Page component for shortfalls view with calculation and sorting
// WHY:  Provides interface for viewing and managing time shortfalls by workspace/aspect
// DOMAIN: Productivity → TimeTracQ
// DEPENDENCIES: ShortfallsTab, useSupabaseTasks, useWorkspaceConfig, core.invoke
// RISKS:
//   - NOTE: Shortfall calculation depends on backend - may fail silently
//   - NOTE: View mode state is managed locally - may not persist

import type { ShortfallEntry } from "@/domains/productivity/timetracq/hooks/useDataFetcher";

import { core } from "@tauri-apps/api";
import React, { useState, useEffect } from "react";

import { useSupabaseTasks } from "../../hooks/data/useSupabasetasks";
import { ShortfallsTab } from "../../screen/tabs/ShortfallsTab";

/**
 * WHAT:
 *   Extracts a number value from unknown input.
 *
 * WHY:
 *   Provides consistent number extraction logic for shortfall mapping.
 *
 * @param value - Value to check
 *
 * @returns Number value or 0
 *
 */
const getNumberValue = (value: unknown): number => {
  return typeof value === "number" ? value : 0;
};

/**
 * WHAT:
 *   Page component that displays shortfalls (deficits between planned and worked hours)
 *   with filtering and sorting capabilities.
 *
 * WHY:
 *   Enables users to identify areas where planned hours exceed worked hours and
 *   prioritize work accordingly.
 *
 * BEHAVIOR:
 *   - Loads tasks and workspace configuration
 *   - Calculates shortfalls via backend
 *   - Maps backend data to frontend format
 *   - Sorts shortfalls based on view mode (today/full)
 *   - Displays shortfalls in ShortfallsTab component
 *
 * RISKS:
 *   - NOTE: Shortfall calculation depends on backend - may fail silently
 *   - NOTE: View mode state is managed locally - may not persist
 *
 * @returns {JSX.Element} The shortfalls page component
 *
 */
export const ShortfallsPage: React.FC = () => {
  const { tasks } = useSupabaseTasks();
  const [shortfalls, setShortfalls] = useState<ShortfallEntry[]>([]);
  const [shortfallViewMode, setShortfallViewMode] = useState<"full" | "today">("today");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchShortfalls = async () => {
      if (tasks.length === 0) {
        setIsLoading(false);
        return;
      }

      try {
        const raw = await core.invoke<{
          shortfalls: {
            parent: string;
            child: string;
            [key: string]: unknown;
          }[];
          worked_today: number;
        }>("calculate_shortfalls", { appHandle: {} });

        // NOTE: Map backend shortfall data to frontend format
        const mappedShortfalls: ShortfallEntry[] = raw.shortfalls.map((item) => {
          return {
            child: item.child,
            parent: item.parent,
            planned: getNumberValue(item.planned_hours_monthly),
            plannedUntilToday: getNumberValue(item.planned_until_today),
            shortfallFullMonth: getNumberValue(item.shortfall_full_month),
            shortfallUntilToday: getNumberValue(item.shortfall_until_today),
            status: typeof item.status === "string" ? item.status : "include",
            worked: getNumberValue(item.total_hours_worked),
          };
        });

        // NOTE: Sort shortfalls by selected view mode (descending)
        mappedShortfalls.sort((a, b) => {
          const aValue =
            shortfallViewMode === "today" ? a.shortfallUntilToday : a.shortfallFullMonth;
          const bValue =
            shortfallViewMode === "today" ? b.shortfallUntilToday : b.shortfallFullMonth;
          return bValue - aValue;
        });

        setShortfalls(mappedShortfalls);
      } catch {
        // NOTE: Error loading shortfalls - reset to empty state
        setShortfalls([]);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchShortfalls();
  }, [tasks, shortfallViewMode]);

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4" />
          <p className="text-[#94A3B8]">Lade Shortfalls...</p>
        </div>
      </div>
    );
  }

  return (
    <ShortfallsTab
      setShortfallViewMode={setShortfallViewMode}
      shortfalls={shortfalls}
      shortfallViewMode={shortfallViewMode}
    />
  );
};
